import { Divider, Form, Select, type SelectProps, Typography } from 'antd'
import React, { type CSSProperties, type ReactNode } from 'react'
import styled from 'styled-components'
import { Text } from '../../../general'
import type { BaseInputProps } from '../../../types/types'

export type InputSelectProps = {
  layout?: 'column' | 'row'
  required?: boolean
  type?: string
  errorMessage?: string
  options?: any[]
  groupedOptions?: any[]
  style?: CSSProperties
  formItemstyle?: CSSProperties
  mode?: 'tags' | 'multiple'
  tooltip?: string
  showSearch?: boolean
  defaultValue?: SelectProps['defaultValue']
  isLoading?: boolean
  hasClear?: boolean
  listHeight?: number
  dropdownStyle?: CSSProperties
  virtual?: boolean
  onSearch?: (value: string) => void
  helperText?: string
  helperTextElement?: ReactNode
  notFoundContent?: ReactNode
  testId: string
  /** Accepts ReactNode so callers can pass labelled nodes (e.g. with asterisks). */
  label?: ReactNode
} & Omit<BaseInputProps, 'label'>

function InputSelect({
  value,
  label,
  disabled,
  onChange,
  layout,
  required,
  type,
  errorMessage,
  placeholder,
  options,
  groupedOptions,
  style,
  formItemstyle,
  mode,
  tooltip,
  showSearch,
  className,
  defaultValue,
  hasClear = false,
  listHeight = 256,
  dropdownStyle,
  virtual = false,
  helperText,
  helperTextElement,
  testId,
  ...props
}: InputSelectProps) {
  const handleClear = () => {
    if (onChange) {
      onChange(null)
    }
  }

  const renderOptionContent = (option: any) => {
    if (option.image) {
      return (
        <OptionContainer>
          <ImageContainer>{option.image}</ImageContainer>
          <StyledText>{option.label}</StyledText>
        </OptionContainer>
      )
    }
    return option.label
  }

  const hasHelper = !!helperTextElement || !!helperText
  const labelNode = hasHelper ? (
    <>
      {label}
      {helperTextElement ? (
        <HelperText>{helperTextElement}</HelperText>
      ) : (
        <HelperText>{helperText}</HelperText>
      )}
    </>
  ) : (
    label
  )

  return (
    <StyledFormItem
      data-testid={testId}
      className={className}
      name={type}
      label={labelNode}
      required={required}
      $hasHelper={hasHelper}
      rules={[
        {
          required,
          ...(errorMessage && { message: errorMessage })
        }
      ]}
      labelCol={layout === 'column' ? { span: 24 } : {}}
      wrapperCol={layout === 'column' ? { span: 24 } : {}}
      style={formItemstyle}
      tooltip={tooltip}
      initialValue={defaultValue}
    >
      <Select
        data-testid={`${testId}-select`}
        defaultValue={defaultValue}
        showSearch={showSearch}
        {...props}
        value={value}
        mode={mode}
        style={{ ...style, width: '100%' }}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        optionFilterProp="label"
        listHeight={listHeight}
        dropdownStyle={dropdownStyle}
        virtual={virtual}
        dropdownRender={
          hasClear
            ? (menuOptions) => (
                <>
                  {menuOptions}
                  <StyledDivider />
                  <ButtonsContainer>
                    <StyledButton
                      onClick={(e) => {
                        e.preventDefault()
                        handleClear()
                      }}
                    >
                      <Text level={1}>Clear</Text>
                    </StyledButton>
                  </ButtonsContainer>
                </>
              )
            : undefined
        }
      >
        {options &&
          options.map((option) => (
            <Select.Option
              key={String(option.value)}
              value={option.value}
              label={option.label}
            >
              {renderOptionContent(option)}
            </Select.Option>
          ))}
        {groupedOptions &&
          groupedOptions.map((group) => (
            <Select.OptGroup key={group.label} label={group.label}>
              {group.options.map((option: any) => (
                <Select.Option
                  key={String(option.value)}
                  value={option.value}
                  label={option.label}
                >
                  {renderOptionContent(option)}
                </Select.Option>
              ))}
            </Select.OptGroup>
          ))}
      </Select>
    </StyledFormItem>
  )
}

const HelperText = styled(Typography.Text)`
  width: 100%;
  font-weight: 400 !important;
  font-size: 13px;
  color: ${({ theme }) => theme.color.neutral.color4};
  line-height: 1.4;
  margin-bottom: -12px;
`

const StyledFormItem = styled(Form.Item)<{ $hasHelper?: boolean }>`
  ${({ $hasHelper }) =>
    $hasHelper &&
    `
    .ant-form-item-label {
      overflow: visible;
      white-space: normal;
      height: auto;
      padding-bottom: 0;
    }

    .ant-form-item-label > label {
      height: auto;
      flex-wrap: wrap;
    }
  `}
`

const StyledButton = styled.button`
  all: unset;
  cursor: pointer;
  color: ${(props) => props.theme.color.status.primary};
  :hover {
    opacity: 0.7;
  }
`

const ButtonsContainer = styled.div`
  padding: 8px 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const StyledDivider = styled(Divider)`
  margin: 4px 0;
`

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: normal;
  word-break: break-word;
`

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default InputSelect
